'use client';

import {
  Text,
  Stack,
  Accordion,
  HStack,
  Box,
  Button,
  Spinner,
} from '@chakra-ui/react';
import { NestedHome } from '@habity/types';
import { ProfileTable } from '../../../../ui/tables/Table';
import { Viewings } from './Viewings';
import { hasPrice } from '~/lib/homes';
import { getBiddingHistoryData } from '~/lib/homes/bidding';
import { useTranslations } from 'next-intl';
import {
  getPrice,
  shouldShowHighestBid,
  shouldShowBidHistory,
  hasBiddingActive,
  shouldShowPrice,
  shouldShowAddress,
  isSold,
  hasSubscribedFinalPrice,
} from '@habity/services';
import { capitalizeFirstLetter, formatCurrency } from '@habity/utils';
import { AccordionTabItem } from '~/components/ui/tabs/AccordionTabItem';
import { useUserAction } from '~/hooks/useUserAction';
import { useUser } from '~/hooks/useUser';
import { ICONS } from '~/constants';
import { useState, useEffect } from 'react';

interface HomePriceDetailsProps {
  home: NestedHome;
  onOpenViewingModal?: (viewingId?: number) => void;
}

export const HomePriceDetails = ({
  home,
  onOpenViewingModal,
}: HomePriceDetailsProps) => {
  const { status, price, viewings, provider_data = {} } = home || {};

  const t = useTranslations('Home');
  const { executeAction, isLoading: isSubscribingFinalPrice } = useUserAction();
  const { user } = useUser();

  const [isSubscribedFinalPrice, setIsSubscribedFinalPrice] = useState<boolean>(
    user ? hasSubscribedFinalPrice(user, home.id) : false
  );

  // Sync with server state when user data changes
  useEffect(() => {
    if (user) {
      setIsSubscribedFinalPrice(hasSubscribedFinalPrice(user, home.id));
    } else {
      setIsSubscribedFinalPrice(false);
    }
  }, [user?.id, user?.interests, home.id]);

  // Show "Bevaka slutpris" button based on same conditions as dynamic CTAs
  // Don't show on sold homes
  const showSubscribeFinalPrice =
    shouldShowPrice(home) && shouldShowAddress(home) && !isSold(home);

  const handleSubscribeFinalPrice = async () => {
    if (isSubscribedFinalPrice) return;
    const result = await executeAction({
      home,
      action: 'subscribed_final_price',
    });
    if (result.success && !result.needsAuth) {
      setIsSubscribedFinalPrice(true);
    }
  };

  const customPriceText = (provider_data as TODO)?.price?.text;
  const currentPriceLabel = hasBiddingActive(home)
    ? t('Price.bidding-active')
    : status?.name === 'sold'
    ? t('Price.sales-price')
    : customPriceText || t('Price.starting-price');

  const startingPriceLabel = hasBiddingActive(home)
    ? capitalizeFirstLetter(customPriceText) || t('Price.starting-price')
    : null;

  return (
    <Stack gap="8">
      {/* Price */}
      <Stack spacing="1.5">
        {/* Label */}
        <Text
          size={{ mobile: 'sm', desktop: 'md' }}
          fontWeight="light"
          color="blackAlpha.800"
        >
          {capitalizeFirstLetter(currentPriceLabel)}
        </Text>

        {/* Price */}
        <HStack alignItems="center" spacing={{ mobile: '1', desktop: '3' }}>
          <Box backgroundColor="burntOrange" rounded="full" boxSize="10px" />
          <Text size={{ mobile: '2xl', desktop: '4xl' }} letterSpacing="-1.5px">
            {getPrice(home, hasBiddingActive(home) ? true : false)}
          </Text>
        </HStack>

        {/* Highest bid */}
        {hasBiddingActive(home) && (
          <Text size="sm" color="blackAlpha.700">
            {`${startingPriceLabel}: ${getPrice(home, false)}`}
          </Text>
        )}

        {/* Subscribe to final price button */}
        {showSubscribeFinalPrice && (
          <Button
            variant="secondary"
            size="sm"
            onClick={handleSubscribeFinalPrice}
            leftIcon={
              isSubscribingFinalPrice ? (
                <Spinner size="sm" />
              ) : (
                <ICONS.HOMEBUYERS_ALERT />
              )
            }
            isDisabled={isSubscribingFinalPrice || isSubscribedFinalPrice}
            mt="2"
            w="fit-content"
          >
            {isSubscribedFinalPrice
              ? 'Slutpris bevakas'
              : isSubscribingFinalPrice
              ? t('Price.subscribing')
              : t('Price.subscribe-final-price')}
          </Button>
        )}
      </Stack>

      {/* Bid history */}
      {shouldShowBidHistory(home) && (
        <Accordion
          id="bid-history"
          borderTopWidth="1px"
          borderTopColor="blackAlpha.500"
          allowToggle
        >
          <AccordionTabItem
            title="Budhistorik"
            // variant="profile"
          >
            <ProfileTable
              collapseAfter={10}
              data={getBiddingHistoryData(home)}
              fontSize="xs"
            />
          </AccordionTabItem>
        </Accordion>
      )}

      {/* Viewings */}
      {status?.name !== 'sold' && viewings?.length && (
        <Box mt={{ mobile: '0', desktop: '12' }}>
          <Viewings
            home={home}
            viewings={viewings}
            onOpenViewingModal={onOpenViewingModal}
          />
        </Box>
      )}
    </Stack>
  );
};
